/**
 * CONTACT CARD - Carte réutilisable pour afficher un contact avec actions rapides
 */

import { ROUTES } from '@/lib/routes/constants';
import type { Contact } from '@/types/contact';
import type { CrewMemberRole } from '@/types/crew';
import { Mail, User, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface ContactCardProps {
  contact: Contact;
  onEmailClick?: () => void;
  onCallClick?: () => void;
  clickable?: boolean;
  /** Rôle du membre dans l'équipage (pilote/copilote) */
  role?: CrewMemberRole | null;
  /** Composant pour afficher/modifier le rôle */
  roleContent?: React.ReactNode;
  /** Callback pour retirer le membre */
  onRemove?: () => void;
  /** Tag affiché sous le nom (ex: "Dérogation en attente") */
  tagContent?: React.ReactNode;
  /** Contenu affiché en bas de la carte (ex: checkbox dérogation validée) */
  bottomContent?: React.ReactNode;
}

export function ContactCard({
  contact,
  onEmailClick,
  onCallClick,
  clickable = false,
  roleContent,
  onRemove,
  tagContent,
  bottomContent,
}: ContactCardProps) {
  function handleEmailClick() {
    if (onEmailClick) {
      onEmailClick();
    } else if (contact.email) {
      window.open(`mailto:${contact.email}`, '_blank');
    }
  }

  function handleCallClick() {
    if (onCallClick) {
      onCallClick();
    }
  }

  const contactInfoContent = (
    <>
      <div className="w-10 h-10 rounded-md bg-zinc-200 flex items-center justify-center flex-shrink-0">
        <User className="h-5 w-5 text-zinc-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-900 truncate">
          {contact.first_name} {contact.last_name}
        </p>
        {tagContent && <div className="mt-0.5">{tagContent}</div>}
        <p className="text-sm text-zinc-500 truncate">{contact.phone || 'Aucun téléphone'}</p>
      </div>
    </>
  );

  return (
    <Card className="p-2 gap-2">
      {/* Ligne supérieure : rôle + bouton retirer */}
      {(roleContent || onRemove) && (
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0">{roleContent}</div>
          {onRemove && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              className="p-1 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
              title="Retirer de l'équipage"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      )}
      <div className="flex justify-between items-center">
        {clickable ? (
          <Link
            to={ROUTES.DATA.CONTACTS.DETAIL(contact.id)}
            className="flex items-center gap-3 flex-1 min-w-0 hover:bg-zinc-50 rounded-md transition-colors p-1"
          >
            {contactInfoContent}
          </Link>
        ) : (
          <div className="flex items-center gap-3 p-1">{contactInfoContent}</div>
        )}
        <div className="flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="default" size="sm" onClick={handleEmailClick} disabled={!contact.email}>
            <Mail className="h-4 w-4" />
          </Button>
          <Button variant="default" size="sm" onClick={handleCallClick}>
            Enregistrer un appel
          </Button>
        </div>
      </div>
      {bottomContent && <div className="pt-1 border-t border-zinc-100">{bottomContent}</div>}
    </Card>
  );
}
